// components/features/annonces/AnnonceForm.tsx

import { useState, useEffect } from "react";
import { Annonce } from "../../../types/features/Annonce";
import { 
  Info, AlertTriangle, CheckCircle, AlertCircle, 
  Users, Building2, UserCheck, UserCog, 
  X, Search, Loader2 
} from "lucide-react";
import useAnnonces from "../../../hooks/annonces/useAnnonces";

interface AnnonceFormProps {
  initialData?: Partial<Annonce>;
  onSubmit: (data: any) => void;
  onCancel: () => void;
  isLoading?: boolean;
  isEdit?: boolean;
}

interface Recipient {
  id: string;
  name: string;
  email: string;
  type: "entreprise" | "agent" | "admin";
}

const TYPE_OPTIONS = [
  { value: "info", label: "Information", icon: Info, color: "text-blue-600 bg-blue-50 border-blue-200" },
  { value: "warning", label: "Avertissement", icon: AlertTriangle, color: "text-amber-600 bg-amber-50 border-amber-200" },
  { value: "success", label: "Succès", icon: CheckCircle, color: "text-green-600 bg-green-50 border-green-200" },
  { value: "danger", label: "Urgent", icon: AlertCircle, color: "text-red-600 bg-red-50 border-red-200" },
];

export default function AnnonceForm({
  initialData = {},
  onSubmit,
  onCancel,
  isLoading = false,
  isEdit = false
}: AnnonceFormProps) {
  const { getRecipients, loading } = useAnnonces();
  
  const [formData, setFormData] = useState({
    title: initialData.title || "",
    content: initialData.content || "",
    type: initialData.type || "info",
    send_to_all: initialData.send_to_all ?? true,
    recipients: initialData.recipients || [],
    is_published: initialData.is_published ?? false,
  });

  const [recipientsList, setRecipientsList] = useState<Recipient[]>([]);
  const [selectedRecipients, setSelectedRecipients] = useState<Recipient[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loadingRecipients, setLoadingRecipients] = useState(false);

  // Charger la liste des destinataires
  useEffect(() => {
    loadRecipients();
  }, []);

  useEffect(() => {
    if (formData.recipients && formData.recipients.length > 0 && recipientsList.length > 0) {
      const selected = recipientsList.filter(r => formData.recipients.includes(r.id));
      setSelectedRecipients(selected);
    }
  }, [recipientsList]);

  const loadRecipients = async () => {
    setLoadingRecipients(true);
    try {
      const data = await getRecipients();
      if (data) {
        setRecipientsList(data);
      }
    } catch (error) {
      console.error("Erreur lors du chargement des destinataires", error);
    } finally {
      setLoadingRecipients(false);
    }
  };

  const handleToggleRecipient = (recipient: Recipient) => {
    setSelectedRecipients(prev => {
      const exists = prev.find(r => r.id === recipient.id);
      if (exists) {
        return prev.filter(r => r.id !== recipient.id);
      } else {
        return [...prev, recipient];
      }
    });
  };

  const handleRemoveRecipient = (id: string) => {
    setSelectedRecipients(prev => prev.filter(r => r.id !== id));
  };

  const handleSendToAllToggle = () => {
    setFormData(prev => ({ ...prev, send_to_all: !prev.send_to_all }));
    if (!formData.send_to_all) {
      setSelectedRecipients([]);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const data = {
      ...formData,
      recipients: selectedRecipients.map(r => r.id),
    };
    onSubmit(data);
  };

  const getTypeIcon = (type: string) => {
    return TYPE_OPTIONS.find(t => t.value === type)?.icon || Info;
  };

  const filteredRecipients = recipientsList.filter(r =>
    r.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    r.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const TypeIcon = getTypeIcon(formData.type);
  const selectedType = TYPE_OPTIONS.find(t => t.value === formData.type);

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Titre */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Titre *
        </label>
        <input
          type="text"
          value={formData.title}
          onChange={(e) => setFormData({ ...formData, title: e.target.value })}
          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/50 focus:border-primary outline-none transition-all"
          placeholder="Ex: Nouvelle fonctionnalité disponible"
          required
        />
      </div>

      {/* Type */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Type *
        </label>
        <div className="flex gap-2 flex-wrap">
          {TYPE_OPTIONS.map((type) => {
            const Icon = type.icon;
            const isSelected = formData.type === type.value;
            return (
              <button
                key={type.value}
                type="button"
                onClick={() => setFormData({ ...formData, type: type.value as  Annonce['type'] })}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                  isSelected
                    ? type.color + ' border'
                    : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                }`}
              >
                <Icon size={14} />
                {type.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Contenu */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Contenu *
        </label>
        <textarea
          value={formData.content}
          onChange={(e) => setFormData({ ...formData, content: e.target.value })}
          className={`w-full px-4 py-2.5 border-2 rounded-lg focus:ring-2 focus:outline-none resize-none transition-all ${
            selectedType?.color || 'border-gray-300'
          }`}
          placeholder="Écrivez votre annonce ici..."
          rows={6}
          required
        />
        <div className="flex justify-between text-xs text-gray-400 mt-1">
          <span>{formData.content.length} caractères</span>
          {formData.content && (
            <span className="flex items-center gap-1 text-primary">
              <TypeIcon size={14} />
              Aperçu
            </span>
          )}
        </div>
      </div>

      {/* Aperçu */}
      {formData.content && (
        <div className={`p-4 rounded-lg border ${selectedType?.color || 'bg-gray-50 border-gray-200'}`}>
          <p className="text-xs text-gray-500 mb-2">Aperçu :</p>
          <div className="prose prose-sm max-w-none">
            <p className="text-gray-700 whitespace-pre-wrap">{formData.content}</p>
          </div>
        </div>
      )}

      {/* Destinataires */}
      <div className="border border-gray-200 rounded-lg overflow-hidden">
        <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <label className="text-sm font-medium text-gray-700">
              Destinataires
            </label>
            <button
              type="button"
              onClick={handleSendToAllToggle}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                formData.send_to_all
                  ? 'bg-primary/10 text-primary'
                  : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
              }`}
            >
              <Users size={16} />
              {formData.send_to_all ? 'Envoyer à tous' : 'Sélectionner'}
            </button>
          </div>
        </div>

        <div className="p-4">
          {formData.send_to_all ? (
            <div className="flex items-center gap-3 p-3 bg-green-50 rounded-lg border border-green-200">
              <CheckCircle size={20} className="text-green-600" />
              <div>
                <p className="text-sm font-medium text-green-700">Envoi à tous</p>
                <p className="text-xs text-green-600">
                  L'annonce sera envoyée à <strong>tous</strong> les utilisateurs de la plateforme
                </p>
              </div>
            </div>
          ) : (
            <div>
              {/* Recherche */}
              <div className="relative mb-3">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Rechercher un destinataire..."
                  className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary/50 focus:border-primary outline-none"
                />
              </div>

              {loadingRecipients ? (
                <div className="flex justify-center py-4">
                  <Loader2 className="w-6 h-6 animate-spin text-primary" />
                </div>
              ) : (
                <>
                  {/* Sélectionnés */}
                  {selectedRecipients.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-3">
                      {selectedRecipients.map((recipient) => (
                        <span
                          key={recipient.id}
                          className="inline-flex items-center gap-1 px-2 py-1 bg-primary/10 text-primary rounded-lg text-xs font-medium"
                        >
                          {recipient.name}
                          <button
                            type="button"
                            onClick={() => handleRemoveRecipient(recipient.id)}
                            className="hover:text-red-600 transition-colors"
                          >
                            <X size={12} />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}

                  {/* Liste */}
                  <div className="max-h-48 overflow-y-auto space-y-1 border border-gray-200 rounded-lg p-1">
                    {filteredRecipients.map((recipient) => {
                      const isSelected = selectedRecipients.some(r => r.id === recipient.id);
                      const Icon = recipient.type === 'entreprise' ? Building2 : 
                                  recipient.type === 'agent' ? UserCheck : UserCog;

                      return (
                        <label
                          key={recipient.id}
                          className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-colors ${
                            isSelected ? 'bg-primary/5 border border-primary/20' : 'hover:bg-gray-50'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => handleToggleRecipient(recipient)}
                            className="w-4 h-4 text-primary rounded border-gray-300 focus:ring-primary/50"
                          />
                          <div className={`p-1.5 rounded-full ${
                            recipient.type === 'entreprise' ? 'bg-blue-100' :
                            recipient.type === 'agent' ? 'bg-green-100' : 'bg-purple-100'
                          }`}>
                            <Icon size={14} className={
                              recipient.type === 'entreprise' ? 'text-blue-600' :
                              recipient.type === 'agent' ? 'text-green-600' : 'text-purple-600'
                            } />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-800 truncate">{recipient.name}</p>
                            <p className="text-xs text-gray-400 truncate">{recipient.email}</p>
                          </div>
                          <span className={`text-xs px-2 py-0.5 rounded-full whitespace-nowrap ${
                            recipient.type === 'entreprise' ? 'bg-blue-100 text-blue-700' :
                            recipient.type === 'agent' ? 'bg-green-100 text-green-700' : 'bg-purple-100 text-purple-700'
                          }`}>
                            {recipient.type === 'entreprise' ? 'Entreprise' :
                             recipient.type === 'agent' ? 'Agent' : 'Admin'}
                          </span>
                        </label>
                      );
                    })}
                    {filteredRecipients.length === 0 && (
                      <p className="text-center text-gray-400 py-4 text-sm">
                        Aucun destinataire trouvé
                      </p>
                    )}
                  </div>

                  <p className="text-xs text-gray-400 mt-2">
                    {selectedRecipients.length} destinataire(s) sélectionné(s)
                  </p>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Publication */}
      <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
        <label className="text-sm font-medium text-gray-700">Publier immédiatement</label>
        <button
          type="button"
          onClick={() => setFormData({ ...formData, is_published: !formData.is_published })}
          className={`relative w-12 h-6 rounded-full transition-colors ${
            formData.is_published ? 'bg-green-500' : 'bg-gray-300'
          }`}
        >
          <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${
            formData.is_published ? 'translate-x-6' : ''
          }`} />
        </button>
        <span className="text-sm text-gray-500">
          {formData.is_published ? 'Publiée' : 'Brouillon'}
        </span>
        {formData.is_published && (
          <span className="text-xs text-amber-600 flex items-center gap-1">
            <AlertCircle size={12} />
            Les emails seront envoyés immédiatement
          </span>
        )}
      </div>

      {/* Boutons */}
      <div className="flex justify-end gap-3 pt-4 border-t">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
        >
          Annuler
        </button>
        <button
          type="submit"
          disabled={isLoading}
          className="px-4 py-2 bg-primary text-white text-sm font-medium rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 flex items-center gap-2"
        >
          {isLoading ? (
            <>
              <Loader2 size={16} className="animate-spin" />
              Chargement...
            </>
          ) : (
            isEdit ? 'Mettre à jour' : 'Créer'
          )}
        </button>
      </div>
    </form>
  );
}